import { useState, useEffect, useMemo } from 'react';
import { useLocale } from 'next-intl';
import useSWR from 'swr';
import { getBranchesV2 } from '@/services/branches';

export const useBranches = () => {
  const locale = useLocale();
  const [branchesGroup, setBranchesGroup] = useState({});
  const [allBranches, setAllBranches] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [query, setQuery] = useState('');
  const [availableOnly, setAvailableOnly] = useState(false);
  const [selectedCity, setSelectedCity] = useState(0);

  const cities = Object.keys(branchesGroup || {});

  const { data, isLoading } = useSWR(
    `branches/${locale}`,
    () => getBranchesV2(locale),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 30 * 60 * 1000,
      focusThrottleInterval: 30 * 60 * 1000,
    }
  );

  useEffect(() => {
    const filteredData = data?.data?.items || data?.data;

    if (filteredData) {
      const processedData = filteredData.reduce((acc, branch) => {
        const city =
          branch?.location?.source_region_name || branch?.source_region_name;

        if (!city) return acc;
        if (!acc[city]) {
          acc[city] = [];
        }
        acc[city].push(branch?.location || branch);
        return acc;
      }, {});

      setBranchesGroup(processedData);
      setAllBranches(filteredData);
      setSelectedBranches(Object.values(processedData)?.[0] || []);
    }
  }, [data]);

  // Filter branches across ALL branches based on search and availability
  const filteredBranches = useMemo(() => {
    if (!allBranches.length) return [];

    return allBranches.filter((branch) => {
      const matchesSearch =
        query === '' ||
        branch.name?.toLowerCase().includes(query.toLowerCase()) ||
        branch.address?.toLowerCase().includes(query.toLowerCase()) ||
        branch.city?.toLowerCase().includes(query.toLowerCase()) ||
        branch.region?.toLowerCase().includes(query.toLowerCase());

      const matchesAvailability = !availableOnly || branch.isAvailable;

      return matchesSearch && matchesAvailability;
    });
  }, [allBranches, query, availableOnly]);

  // Determine which branches to display
  const branchesToDisplay = useMemo(() => {
    return query || availableOnly ? filteredBranches : selectedBranches;
  }, [query, availableOnly, filteredBranches, selectedBranches]);

  const handleChangeCity = (city, idx) => {
    setSelectedCity(idx);
    setSelectedBranches(branchesGroup[city] || []);
  };

  const handleSearch = (e) => {
    setQuery(e.target.value);
  };

  const handleToggleAvailableOnly = (checked) => {
    setAvailableOnly(checked);
  };

  return {
    // State
    branchesGroup,
    allBranches,
    selectedBranches,
    cities,
    query,
    availableOnly,
    selectedCity,
    isLoading,

    // Computed values
    branchesToDisplay,
    filteredBranches,

    // Actions
    handleChangeCity,
    handleSearch,
    handleToggleAvailableOnly,
    setSelectedCity,
  };
};
